import AgTechGrid from "@agtech/web/components/Grids/AgTechGrid";
import { AgTechGridHeaderConfiguration } from "@agtech/web/components/Grids/AgTechGridHeader";
import { useAgTechQueryResultProjection } from "@agtech/core/data/queries/AgTechDataQueries";
import { LotLink } from "../../../lots/components/buttons/SaleLotButtons";
import { useLotDataGetSaleLotDataQuery } from "@sales/data/lots/LotDataQueries";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import { SaleLot } from "@sales/data/lots/LotData";
import { AgTechGridEntitySelectionIndex, mergeGridEntitySelections } from "@agtech/web/components/Grids/AgTechGridEntities";

export type InvoiceChangeLotsGridProps = {
    contactId: number,
    availableLots: AgTechGridEntitySelectionIndex<SaleLot>,
    header?: AgTechGridHeaderConfiguration,
    onLotSelected?: (lot: SaleLot) => Promise<void>
    onLotDeSelected?: (lot: SaleLot) => Promise<void>,
    isReadonly?: boolean
}

const InvoiceChangeLotsGrid = (props: InvoiceChangeLotsGridProps) => {
    const gridQuery = useInvoiceChangeLotsGridQuery(props);

    return (
        <AgTechGrid
            data={{
                itemQuery: gridQuery
            }}
            header={props.header}
            columns={[
                {
                    header: 'Lot Number',
                    value: (lot, context) => (
                        <LotLink
                            lot={context.lotData.lots[lot.entity.id]}
                            lotCategories={context.lotData.lotCategories}
                            classes='mx-3 !text-sm'
                        />
                    ),
                    width: '120px'
                },
                {
                    header: 'Description',
                    value: (e, context) => context.lotData.lots[e.entity.id]?.lotDescription
                },
                {
                    header: 'Purchase Price ($)',
                    value: (e, context) => {
                        const correspondingLot = context.lotData.lots[e.entity.id];

                        return correspondingLot && correspondingLot.purchasePrice && correspondingLot.purchasePrice > 0
                            ? AgTechMoneyFormatter.format(correspondingLot.purchasePrice)
                            : correspondingLot.purchasePrice ?? 'N/A';
                    }
                }
            ]}
            isRowEntityTheSame={(prev, curr) => {
                return prev.entity.lotNumber === curr.entity.lotNumber &&
                    prev.entity.invoiceState === curr.entity.invoiceState;
            }}
            selectionConfig={props.isReadonly ? undefined : {
                isItemSelected: lot => lot.isSelected,
                onItemSelected: async (e) => {
                    if (props.onLotSelected) {
                        props.onLotSelected(e.entity);
                    }
                },
                onItemDeSelected: async (e) => {
                    if (props.onLotDeSelected) {
                        props.onLotDeSelected(e.entity);
                    }
                }
            }}
        />
    )
}

const useInvoiceChangeLotsGridQuery = (props: InvoiceChangeLotsGridProps) => {
    const lotDetailsQuery = useLotDataGetSaleLotDataQuery();

    return useAgTechQueryResultProjection({
        queryRun: lotDetailsQuery,
        projection: lotData => {
            return {
                items: mergeGridEntitySelections({
                    entitySelections: props.availableLots,
                    entities: Object.keys(props.availableLots).map(id => lotData.lots[id]),
                }),
                context: {
                    lotData
                }
            }
        }
    });
}

export default InvoiceChangeLotsGrid;